import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  TextField,
} from '@mui/material';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { fi } from 'date-fns/locale';
import { withTranslation } from 'react-i18next';
import axios from 'axios';

// Mirrors InvoiceViewSet.AMILI_FREEZE_MAX_DAYS. The backend is authoritative.
const FREEZE_MAX_DAYS = 90;
const FREEZE_DEFAULT_DAYS = 14;

const addDays = (days) => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + days);
  return date;
};

const toDateString = (date) => {
  if (!date || isNaN(date.getTime())) return '';
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

const formatDay = (dateStr) => (dateStr ? new Date(`${dateStr}T00:00:00`).toLocaleDateString('fi-FI') : '');

/**
 * The action row of the invoice's Maksuvalvonta section: the parent's ohisuoritus buttons
 * (`paymentActions`) first, then Keskeytä perintä / Lopeta keskeytys, and Peru perintä apart at
 * the right edge since it is the one that can cost money. `monitored` says whether Amili is
 * collecting the invoice, which the freeze and cancel actions need. The section heading carries
 * one help popover for all of them.
 */
const AmiliActions = ({ t, invoice, monitored, canManage, paymentActions, onInvoiceUpdated, onNotify }) => {
  const [freezeOpen, setFreezeOpen] = useState(false);
  const [freezeDate, setFreezeDate] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(null);

  const collectionActions = monitored && canManage;
  if (!monitored && !paymentActions) return null;
  const frozenUntil = monitored ? invoice.amili_frozen_until : null;
  const collectionStarted = Boolean(invoice.amili_collection_started);
  const canCancel = invoice.status !== 'paid';

  const errorText = (err) => {
    const code = err.response?.data?.error_code;
    const fallback = err.response?.data?.error || t('amili.actionFailed');
    return code ? t(`amili.error.${code}`, fallback) : fallback;
  };

  const post = async (kind, url, body, successKey) => {
    setBusy(kind);
    setError('');
    try {
      const response = await axios.post(url, body);
      onInvoiceUpdated(response.data);
      onNotify(t(successKey), 'success');
      return true;
    } catch (err) {
      if (kind === 'unfreeze') onNotify(errorText(err), 'error');
      else setError(errorText(err));
      return false;
    } finally {
      setBusy(null);
    }
  };

  const openFreeze = () => {
    setFreezeDate(frozenUntil ? new Date(`${frozenUntil}T00:00:00`) : addDays(FREEZE_DEFAULT_DAYS));
    setError('');
    setFreezeOpen(true);
  };

  const submitFreeze = async () => {
    const ok = await post(
      'freeze',
      `/api/invoices/${invoice.id}/amili_freeze/`,
      { end_date: toDateString(freezeDate) },
      'amili.freezeSaved',
    );
    if (ok) setFreezeOpen(false);
  };

  const endFreeze = () => post(
    'unfreeze',
    `/api/invoices/${invoice.id}/amili_freeze/`,
    { end_date: toDateString(addDays(0)) },
    'amili.freezeEnded',
  );

  const openCancel = () => {
    setReason('');
    setError('');
    setCancelOpen(true);
  };

  const submitCancel = async () => {
    const ok = await post(
      'cancel',
      `/api/invoices/${invoice.id}/amili_cancel/`,
      { reason: reason.trim() },
      'amili.cancelDone',
    );
    if (ok) setCancelOpen(false);
  };

  const freezeValid = freezeDate && !isNaN(freezeDate.getTime())
    && freezeDate >= addDays(1) && freezeDate <= addDays(FREEZE_MAX_DAYS);

  return (
    <Box sx={{ mt: 2 }} data-testid="amili-actions">
      {frozenUntil && (
        <Alert severity="info" sx={{ mb: 1.5 }} data-testid="amili-frozen-info">
          {t('amili.frozenInfo', { date: formatDay(frozenUntil) })}
        </Alert>
      )}
      {(paymentActions || collectionActions) && (
        <>
          <Divider sx={{ mb: 2 }} />
          {/* One row on a desktop; on a phone a stack of full-width buttons, so a long label
              that wraps does not leave the buttons in uneven sizes. */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              alignItems: 'center',
              '& > .MuiButton-root': {
                flex: { xs: '1 1 100%', sm: '0 0 auto' },
                justifyContent: { xs: 'flex-start', sm: 'center' },
                textAlign: { xs: 'left', sm: 'center' },
              },
            }}
          >
            {paymentActions}
            {collectionActions && (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PauseCircleOutlineIcon />}
                  onClick={openFreeze}
                  disabled={Boolean(busy)}
                  data-testid="amili-freeze-button"
                >
                  {frozenUntil ? t('amili.changeFreeze') : t('amili.freeze')}
                </Button>
                {frozenUntil && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={busy === 'unfreeze' ? <CircularProgress size={14} /> : <PlayCircleOutlineIcon />}
                    onClick={endFreeze}
                    disabled={Boolean(busy)}
                    data-testid="amili-unfreeze-button"
                  >
                    {t('amili.endFreeze')}
                  </Button>
                )}
                {canCancel && (
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    startIcon={<CancelOutlinedIcon />}
                    onClick={openCancel}
                    disabled={Boolean(busy)}
                    sx={{ ml: { sm: 'auto' } }}
                    data-testid="amili-cancel-button"
                  >
                    {t('amili.cancelCollection')}
                  </Button>
                )}
              </>
            )}
          </Box>
        </>
      )}

      <Dialog open={freezeOpen} onClose={() => setFreezeOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{frozenUntil ? t('amili.changeFreeze') : t('amili.freeze')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>{t('amili.freezeDescription')}</DialogContentText>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fi}>
            <DatePicker
              label={t('amili.freezeUntil')}
              value={freezeDate}
              onChange={setFreezeDate}
              format="dd.MM.yyyy"
              minDate={addDays(1)}
              maxDate={addDays(FREEZE_MAX_DAYS)}
              slotProps={{ textField: { margin: 'dense', fullWidth: true, inputProps: { 'data-testid': 'amili-freeze-date' } } }}
            />
          </LocalizationProvider>
          {error && <Alert severity="error" sx={{ mt: 2 }} data-testid="amili-action-error">{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFreezeOpen(false)}>{t('Cancel')}</Button>
          <Button
            variant="contained"
            onClick={submitFreeze}
            disabled={!freezeValid || Boolean(busy)}
            data-testid="amili-freeze-submit"
          >
            {busy === 'freeze' ? <CircularProgress size={24} /> : t('Save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('amili.cancelTitle')}</DialogTitle>
        <DialogContent>
          {collectionStarted ? (
            <Alert severity="warning" sx={{ mb: 2 }} data-testid="amili-cancel-fee-warning">
              {t('amili.cancelFeeWarning')}
            </Alert>
          ) : (
            <Alert severity="info" sx={{ mb: 2 }} data-testid="amili-cancel-no-fee">
              {t('amili.cancelNoFee')}
            </Alert>
          )}
          <DialogContentText sx={{ mb: 2 }}>{t('amili.cancelConsequence')}</DialogContentText>
          <TextField
            label={t('amili.cancelReason')}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            required
            inputProps={{ maxLength: 500, 'data-testid': 'amili-cancel-reason' }}
          />
          {error && <Alert severity="error" sx={{ mt: 2 }} data-testid="amili-action-error">{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelOpen(false)}>{t('amili.cancelBack')}</Button>
          <Button
            variant="contained"
            color="error"
            onClick={submitCancel}
            disabled={!reason.trim() || Boolean(busy)}
            data-testid="amili-cancel-submit"
          >
            {busy === 'cancel' ? <CircularProgress size={24} /> : t('amili.cancelConfirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default withTranslation()(AmiliActions);
